import { Component, ElementRef, inject, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { DdEntryMerged } from '../../../core/model/data-dictionary';
import { HisBaseGlobalsService } from '../../../core/service/base/his-base-globals.service';
import { HisDdRefreshService } from '../../../core/service/base/his-dd-refresh.service';
import { HisDdService } from '../../../core/service/base/his-dd.service';
import { getBaseUrl } from '../../../core/utils/his-url.utils';

/**
 * Popup shown to developers/administrators with `dataDictionaryEditMode` enabled, giving access to the
 * data dictionary configuration (identifier, applied contexts, configuration sources, data source, ...)
 * of the form field it is attached to, plus links to add/edit the corresponding metadata.
 *
 * Used exclusively by {@link HisPlamComponent}, which is responsible for positioning the popup (see
 * {@link position}) whenever the mouse enters the surrounding plam row.
 */
@Component( {
    selector: 'his-dd-popup',
    templateUrl: './his-dd-popup.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true
} )
export class HisDdPopupComponent {
    hisBaseGlobalsService = inject( HisBaseGlobalsService );
    private hisDdService = inject( HisDdService );
    private hisDdRefreshService = inject( HisDdRefreshService );

    /** Name der ConfSrc "HIS" (siehe ApplyMetadata.CONFSRC_NAME_HIS im JSF-Framework) */
    private static readonly CONFSRC_NAME_HIS = 'HIS';

    @Input() ddIdentifier: string;
    @Input() ddContexts: string[];
    @Input() dataSource: string;

    @ViewChild( 'popupRef' ) private popupRef: ElementRef<HTMLElement>;

    /**
     * Positions the popup horizontally centered at the given x-offset (relative to the surrounding
     * container). Called by {@link HisPlamComponent} on mouseenter. Vertically the popup stays below
     * the field via CSS (top: 100%).
     */
    position( offsetX: number ): void {
        const popupEl = this.popupRef?.nativeElement;
        if ( !popupEl ) {
            return;
        }
        popupEl.style.left = `${offsetX - popupEl.offsetWidth / 2}px`;
    }

    getAppliedContexts(): string[] {
        return this.ddContexts || ['DEFAULT'];
    }

    /**
     * Returns the names of the configuration sources (e.g. 'HIS', 'CUST') that provide an explicit data dictionary
     * configuration for the given context (not merged with 'DEFAULT'). Used to decide whether an "Edit"-link
     * for an existing configuration should be offered, in addition to the "Add Config"-link.
     */
    getConfSrcsForContext( context: string ): string[] {
        if ( !this.ddIdentifier ) {
            return [];
        }
        const entry = this.hisDdService.getEntryForContext( this.ddIdentifier, context );
        return entry?.confSrcs || [];
    }

    /**
     * Analog zu ApplyMetadata#debugMetadata (Java): Der "HIS"-ConfSrc darf nur im HIS-Development-Modus
     * bearbeitet werden. Außerhalb davon wird statt "Edit" nur "Show" (read-only) angeboten.
     */
    isConfSrcReadonly( confSrc: string ): boolean {
        return confSrc?.toUpperCase() === HisDdPopupComponent.CONFSRC_NAME_HIS && !this.hisBaseGlobalsService.system.hisDevelopmentMode;
    }

    getEffectiveConfigSummary(): string {
        const entry = this.getDdEntry();
        if ( !entry ) {
            return 'none';
        }
        //console.log('getEffectiveConfigSummary - ddentry:',entry);
        const properties = [];
        if ( entry.labeltextIdentifier ) properties.push( 'label' +'='+ entry.labeltextIdentifier );
        if ( entry.helptextIdentifier ) properties.push( 'helptext' +'='+ entry.helptextIdentifier );
        if ( entry.required ) properties.push( 'required' +'='+ entry.required );
        if ( entry.readonly ) properties.push( 'readonly' +'='+ entry.readonly );
        if ( entry.defaultValue !== undefined ) properties.push( 'defaultValue' +'='+ entry.defaultValue );
        if ( entry.minValueInteger !== undefined ) properties.push( 'minValueInteger' +'='+ entry.minValueInteger );
        if ( entry.maxValueInteger !== undefined ) properties.push( 'maxValueInteger' +'='+ entry.maxValueInteger );
        if ( entry.minLength !== undefined ) properties.push( 'minLength' +'='+ entry.minLength );
        if ( entry.maxLength !== undefined ) properties.push( 'maxLength' +'='+ entry.maxLength );
        if ( entry.datatype ) properties.push( 'datatype' +'='+ entry.datatype );
        if ( entry.regexPattern ) properties.push( 'regexPattern' +'='+ entry.regexPattern );
        if ( properties.length === 0 ) {
            return 'none';
        }
        return properties.join( ', ' );
    }

    // wird nur zum Debuggen aufgerufen...
    private getDdEntry(): DdEntryMerged {
        if ( this.ddIdentifier ) {
            return this.hisDdService.getMergedEntry( this.ddIdentifier, this.ddContexts ) || { identifier: this.ddIdentifier, hidden: undefined };
        }
        return null;
    }

    openDatadictionaryEditPopup( identifier: string, context: string ): void {
        const url = this.getDatadictionaryEditUrl( identifier, context );
        this.openPopup( url );
    }

    /**
     * Opens a popup window to edit an already existing data dictionary configuration for the given
     * identifier/context/confSrc.
     */
    openDatadictionaryExistingConfigEditPopup( identifier: string, context: string, confSrc: string ): void {
        const url = this.getDatadictionaryExistingConfigEditUrl( identifier, context, confSrc );
        this.openPopup( url );
    }

    private getDatadictionaryEditUrl( identifier: string, context: string ): string {
        const baseUrl = getBaseUrl();
        return baseUrl + '/pages/startFlow.xhtml?_flowId=datadictionary-create-metadata-flow&identifier=' + encodeURIComponent( identifier ) + '&context=' + encodeURIComponent( context ) + '&popup=true';
    }

    /**
     * URL to edit an already existing data dictionary configuration for the given identifier/context/confSrc.
     * see de.his.appserver...datadictionary-edit-metadata-flow (requires an existing confSrc, unlike the
     * create-metadata-flow used by {@link getDatadictionaryEditUrl}).
     */
    private getDatadictionaryExistingConfigEditUrl( identifier: string, context: string, confSrc: string ): string {
        const baseUrl = getBaseUrl();
        return baseUrl + '/pages/startFlow.xhtml?_flowId=datadictionary-edit-metadata-flow&identifier=' + encodeURIComponent( identifier )
            + '&context=' + encodeURIComponent( context ) + '&confSrc=' + encodeURIComponent( confSrc ) + '&popup=true';
    }

    /**
     * Opens a popup window and starts watching it (see HisDdRefreshService). When the popup has
     * been closed, the data dictionary is reloaded from the backend and re-applied to the current
     * form fields, so that newly created or changed configurations take effect without a full
     * page reload.
     */
    private openPopup( url: string ): void {
        const width = 1024;
        const height = 768;
        const left = Math.round( ( window.screen.width - width ) / 2 + window.screenX );
        const top = Math.round( ( window.screen.height - height ) / 2 + window.screenY );
        const popupWindow = window.open( url, '_blank', `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,status=yes,toolbar=no,menubar=no,location=no` );
        this.hisDdRefreshService.watchForDdRefresh( popupWindow );
    }
}
